import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ADMIN › ELLENŐRZÉS › KÖNYVELETLEN ÓRÁK (backend/munkaido-elszamolas.js › GET /konyveletlen).
 *
 *  Naplóellenőrzés: egy időszakban (alapból a hónap eleje → ma) kinek milyen órája nincs még
 *  beírva a naplóba. Kollégánként a darabszám (a legtöbb elöl), kinyitva az órák listája. A
 *  szabály a havi munkaidő-elszámolás gombjáé – ami itt könyveletlen, az a kolléga elszámolásának
 *  nyomtatását is tiltja –, ezért a lista a kiszolgálóról jön, a felület nem számol újra.
 *  A PDF a kiszolgáló riport-motorjával készül (DataService.riportPdf).
 */

interface KonyveletlenOra {
  datum: string;
  ora: number;
  hossz: number;
  kezdes: string | null;
  vege: string | null;
  tantargy: string;
  egyseg: string;
  helyettesites: boolean;
  helyettesites_mod: string | null;
  eredeti_tanar: string | null;
}

interface KonyveletlenKollega {
  adid: string;
  nev: string;
  db: number;
  napok: number;
  orak: KonyveletlenOra[];
}

interface KonyveletlenValasz {
  tol: string;
  ig: string;
  ma: string;
  tanar: string | null;
  konyveletlen: number;
  kollegak: KonyveletlenKollega[];
  tanarok: { adid: string; nev: string }[];
}

const NAPNEV = ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'];

@Component({
  selector: 'app-admin-konyveletlen-orak',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './konyveletlen-orak.component.html',
  styleUrls: ['./konyveletlen-orak.component.scss'],
})
export class AdminKonyveletlenOrakComponent implements OnInit {
  intezmeny = '';
  betolt = false;
  pdfKeszul = false;
  hiba = '';
  adat: KonyveletlenValasz | null = null;

  tol = '';
  ig = '';
  tanar = '';
  /** A szűrő választéka – az utolsó lekérés kollégái (a szűrt lekérésnél is mindenki jön). */
  tanarok: { adid: string; nev: string }[] = [];
  nyitva: Record<string, boolean> = {};

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '');
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    //  Az alapidőszakot (hónap eleje → ma, budapesti idő) a kiszolgáló adja vissza.
    await this.toltes();
  }

  async toltes() {
    if (!this.intezmeny) return;
    if (this.tol && this.ig && this.tol > this.ig) { this.hiba = 'A kezdőnap nem lehet későbbi a záró napnál.'; return; }
    this.betolt = true; this.hiba = '';
    try {
      const params: Record<string, string> = {};
      if (this.tol) params['tol'] = this.tol;
      if (this.ig) params['ig'] = this.ig;
      if (this.tanar) params['tanar'] = this.tanar;
      const v = await firstValueFrom(this.http.get<KonyveletlenValasz>(
        `/api/munkaido-elszamolas/konyveletlen/${this.intezmeny}`, { params }));
      this.adat = v;
      //  A kiszolgáló a záró napot a mai napra vágja – a mezők a ténylegesen lekért időszakot mutatják.
      this.tol = v.tol; this.ig = v.ig;
      this.tanarok = v.tanarok || [];
      this.nyitva = {};
      if (v.kollegak.length === 1) this.nyitva[v.kollegak[0].adid] = true;
    } catch (e: any) {
      this.adat = null;
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a kimutatást.';
    } finally { this.betolt = false; }
  }

  /** Gyors időszak: ez a hónap / előző hónap / ez a hét (a záró nap legfeljebb ma). */
  gyors(mod: 'honap' | 'elozo' | 'het') {
    const ma = this.adat?.ma || this.helyiNap(new Date());
    const [y, m] = ma.split('-').map(Number);
    if (mod === 'honap') { this.tol = `${ma.slice(0, 8)}01`; this.ig = ma; }
    else if (mod === 'elozo') {
      const ev = m === 1 ? y - 1 : y;
      const ho = m === 1 ? 12 : m - 1;
      this.tol = `${ev}-${String(ho).padStart(2, '0')}-01`;
      this.ig = this.helyiNap(new Date(ev, ho, 0));
    } else {
      const d = new Date(`${ma}T12:00:00`);
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      this.tol = this.helyiNap(d); this.ig = ma;
    }
    this.toltes();
  }

  private helyiNap(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  valt(k: KonyveletlenKollega) { this.nyitva[k.adid] = !this.nyitva[k.adid]; }
  mindNyitva() { return !!this.adat?.kollegak.length && this.adat.kollegak.every((k) => this.nyitva[k.adid]); }
  mindValt() {
    const nyit = !this.mindNyitva();
    this.nyitva = {};
    if (nyit) for (const k of this.adat?.kollegak || []) this.nyitva[k.adid] = true;
  }

  datumSzoveg(d: string) { return `${d.slice(0, 4)}.${d.slice(5, 7)}.${d.slice(8, 10)}.`; }
  napNev(d: string) { return NAPNEV[new Date(`${d}T12:00:00`).getDay()]; }
  oraSzoveg(o: KonyveletlenOra) { return o.hossz > 1 ? `${o.ora}–${o.ora + o.hossz - 1}. óra` : `${o.ora}. óra`; }
  idopont(o: KonyveletlenOra) { return o.kezdes ? `${o.kezdes}–${o.vege || ''}` : ''; }
  jeloles(o: KonyveletlenOra) {
    if (!o.helyettesites) return '';
    const fajta = o.helyettesites_mod === 'O' ? 'Összevonás' : 'Helyettesítés';
    return o.eredeti_tanar ? `${fajta} (${o.eredeti_tanar} helyett)` : fajta;
  }
  idoszakSzoveg() { return this.adat ? `${this.datumSzoveg(this.adat.tol)} – ${this.datumSzoveg(this.adat.ig)}` : ''; }
  tanarNev() { return this.tanarok.find((t) => t.adid === this.adat?.tanar)?.nev || this.adat?.tanar || ''; }

  /*  PDF: elöl az összesítő (kolléga, óra, nap), utána kollégánként csoportsorral az órák. A
   *  riport-motor 6000 soros korlátja egy iskola egy hónapjára bőven elég; ha mégsem, a
   *  részletező a korlátig tart, és a lap alján megjegyzés jelzi a csonkítást. */
  async pdf() {
    if (!this.adat || this.pdfKeszul) return;
    const a = this.adat;
    const MAX_RESZLET = 5600;
    const reszlet: any[] = [];
    let kihagyott = 0;
    for (const k of a.kollegak) {
      if (reszlet.length + k.orak.length + 1 > MAX_RESZLET) { kihagyott += k.db; continue; }
      reszlet.push({ csoport: `${k.nev} – ${k.db} óra, ${k.napok} nap` });
      for (const o of k.orak) {
        reszlet.push([`${this.datumSzoveg(o.datum)} ${this.napNev(o.datum)}`, this.oraSzoveg(o), this.idopont(o),
          o.egyseg || '—', o.tantargy || '—', this.jeloles(o)]);
      }
    }
    const szuro = a.tanar ? this.tanarNev() : 'mindenki';
    const fajlnev = `Konyveletlen-orak_${a.tol}_${a.ig}${a.tanar ? `_${this.tanarNev().replace(/[^\p{L}\p{N}]+/gu, '_')}` : ''}.pdf`;
    this.pdfKeszul = true;
    try {
      await this.data.riportPdf({
        intezmeny: this.intezmeny,
        cim: 'Könyveletlen órák',
        cimKieg: 'naplóellenőrzés',
        alcim: `Időszak: ${this.idoszakSzoveg()}`,
        adatsav: [['Kolléga', szuro],
          ['Könyveletlen óra', String(a.konyveletlen)], ['Érintett kolléga', String(a.kollegak.length)]],
        lapmeret: 'A4', tajolas: 'allo', betumeret: 8.5,
        tablak: [
          {
            cim: 'Összesítés', osszegzes: `${a.konyveletlen} óra`,
            ures: 'Minden óra be van írva.',
            oszlopok: [{ nev: 'Kolléga', szelesseg: '60%' }, { nev: 'Könyveletlen óra', igazitas: 'jobb', szelesseg: '20%' },
              { nev: 'Érintett nap', igazitas: 'jobb', szelesseg: '20%' }],
            sorok: a.kollegak.map((k) => [{ t: k.nev, fej: true }, String(k.db), String(k.napok)]),
            osszesen: a.kollegak.length ? ['Összesen', String(a.konyveletlen), ''] : undefined,
          },
          ...(reszlet.length ? [{
            cim: 'Könyveletlen órák kollégánként',
            oszlopok: [{ nev: 'Nap', szelesseg: '19%' }, { nev: 'Óra', igazitas: 'bal', szelesseg: '10%' }, { nev: 'Időpont', szelesseg: '12%' },
              { nev: 'Osztály / csoport', szelesseg: '20%' }, { nev: 'Tantárgy', szelesseg: '19%' }, { nev: 'Megjegyzés', szelesseg: '20%' }],
            sorok: reszlet,
          }] : []),
        ],
        lablec: {
          kelt: true,
          megjegyzes: 'Könyveletlen az az eltelt óra, amelynek nincs beírt témája vagy nincs könyvelve. Nem számít az elmaradt óra, '
            + 'a más által megtartott (helyettesített) óra, és a kolléga saját órája a távolléte (szabadság, betegség) napján.'
            + (kihagyott ? ` A részletezőből terjedelmi okból ${kihagyott} óra kimaradt – szűkebb időszakra vagy kollégára kérje le.` : ''),
        },
        fajlnev,
      }, fajlnev, 'letolt');
    } catch (e: any) {
      this.data.sendToast(e?.message || 'A PDF elkészítése nem sikerült.', 'error');
    } finally { this.pdfKeszul = false; }
  }
}
